import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROLE_LABELS, useApp } from "../context/AppContext";
import logoIcon from "../assets/logo_icon.png";

export function LoginPage() {
  const { api, setSession } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // When the server replies with multiple tenants for this email,
  // we keep them here and ask the user to pick one.
  const [tenantChoices, setTenantChoices] = useState(null);

  // If the caller routed us here with a hint, pre-select that tenant_id.
  const [presetTenantId, setPresetTenantId] = useState(null);
  useEffect(() => {
    if (location.state && location.state.tenant_id) {
      setPresetTenantId(location.state.tenant_id);
    }
  }, [location.state]);

  const finishLogin = (resp) => {
    setSession({ token: resp.token, expires_at: resp.expires_at, user: resp.user });
    navigate("/", { replace: true });
  };

  const submit = async (overrides = {}) => {
    setError(null);
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
      };
      const tenant_id = overrides.tenant_id || presetTenantId;
      if (tenant_id) payload.tenant_id = tenant_id;

      const resp = await api.login(payload);

      if (resp && resp.choose_tenant && Array.isArray(resp.tenants)) {
        setTenantChoices(resp.tenants);
        return;
      }
      finishLogin(resp);
    } catch (err) {
      setError(err && err.message ? err.message : "sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  const onPickTenant = (tenant_id) => submit({ tenant_id });

  return (
    <div className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <div className="card w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <img src={logoIcon} alt="ainaopen" className="h-10 w-10 rounded-md object-contain" />
          <div>
            <h1 className="text-xl font-semibold leading-tight">Sign in</h1>
            <p className="text-sm text-slate-500">ainaopen vision admin</p>
          </div>
        </div>

        {!tenantChoices && (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="label">Password</label>
              <input
                type="password"
                className="input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="current-password"
                required
              />
            </div>
            {presetTenantId && (
              <div className="text-xs text-slate-500">
                Signing in to tenant <span className="font-medium text-slate-700">{presetTenantId}</span>{" "}
                <button
                  type="button"
                  className="text-brand-600 hover:underline"
                  onClick={() => setPresetTenantId(null)}
                >
                  change
                </button>
              </div>
            )}
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
            {/* Self-registration is closed (22 Oct): a tenant admin adds users. */}
            <p className="text-center text-sm text-slate-500">New here? Ask your tenant admin for an account.</p>
          </form>
        )}

        {tenantChoices && (
          <div className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">Choose a tenant</h2>
              <p className="text-sm text-slate-500">
                Your email is registered in multiple tenants. Pick the one you want to sign in to.
              </p>
            </div>
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {tenantChoices.map((t) => (
                <li key={t.tenant_id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onPickTenant(t.tenant_id)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-60"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">{t.display_name}</div>
                      <div className="truncate text-xs text-slate-500">
                        {t.tenant_id}
                        {Array.isArray(t.roles) && t.roles.length > 0 && (
                          <>
                            {" · "}
                            {t.roles.map((r) => ROLE_LABELS[r] ?? r).join(", ")}
                          </>
                        )}
                      </div>
                    </div>
                    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                setTenantChoices(null);
                setError(null);
              }}
            >
              Use a different account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
